import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { Currency } from "_shared/money/currencies/currency";
import { WorkTeamSide } from "work/work-agreements/teams/leader-handover/_schemas/team-leader-handover-schema";
import { WorkTeamRole } from "work/work-records/work-team-role";
import { z } from "zod";

/** The same optional type under each of `keys`. */
export function optionalEach<const K extends string, T extends z.ZodType>(
  keys: readonly K[],
  type: T,
): { [P in K]: z.ZodOptional<T> } {
  return Object.fromEntries(keys.map((key) => [key, type.optional()])) as { [P in K]: z.ZodOptional<T> };
}

/** Which keys of a criteria object hold an id and which an instant; the rest pass through as they are. */
export type WorkCriteriaKeys = Readonly<{ ids: readonly string[]; instants: readonly string[] }>;

/** Criteria read from JSON: each id a `Guid` and each instant an `AHDate`. */
export function decodeWorkCriteria<T>(json: Readonly<Record<string, unknown>>, keys: WorkCriteriaKeys): T {
  return Object.fromEntries(
    Object.entries(json).map(([key, value]) => {
      if (keys.ids.includes(key)) return [key, Guid.fromJSON(value) ?? undefined];
      if (keys.instants.includes(key)) return [key, AHDate.fromJSON(value) ?? undefined];
      return [key, value];
    }),
  ) as T;
}

/** Criteria written as JSON: each id and each instant as the string the API reads. */
export function encodeWorkCriteria<T>(props: Readonly<Record<string, unknown>>): T {
  return Object.fromEntries(
    Object.entries(props).map(([key, value]) => {
      if (value instanceof Guid || value instanceof AHDate) return [key, value.toJSON()];
      return [key, value];
    }),
  ) as T;
}

const TEXT_KEYS = [
  "query",
  "name",
  "description",
  "feeScheduleCategoryName",
  "feeScheduleCategoryDescription",
  "teamTemplateName",
  "feeScheduleTemplateName",
] as const;
const ID_KEYS = [
  "RFPId",
  "clientUserId",
  "clientEntityId",
  "clientTeamMemberUserId",
  "clientTeamMemberEntityId",
  "vendorUserId",
  "vendorEntityId",
  "vendorTeamMemberEntityId",
  "vendorTeamMarketplaceId",
  "additionalVendorUserId",
  "additionalVendorEntityId",
  "teamMemberUserId",
  "counterpartyEntityId",
  "feeScheduleCategoryId",
  "feeAssignedMemberUserId",
  "teamTemplateId",
  "feeScheduleTemplateId",
  "userId",
] as const;
const INSTANT_KEYS = ["minStart", "maxStart", "minEnd", "maxEnd", "inProgressOn"] as const;
const YES_NO_KEYS = ["hasEnd", "vendorTeamRestricted", "feeIsFixed", "feeIsHourly"] as const;
const AMOUNT_KEYS = ["minFeeRateAmount", "maxFeeRateAmount", "minDiscount", "maxDiscount"] as const;

/**
 * The criteria a proposal list and a work agreement list share, as the browser holds them.
 *
 * `Work.WorkRecords.WorkCriteria` on the server, less what the server sets. The proposal and work
 * agreement filter schemas spread these shapes and add their own, so a criterion the two share is
 * declared once and cannot be read by one list and dropped by the other.
 */
export default class WorkCriteriaSchema {
  public static readonly KEYS: WorkCriteriaKeys = { ids: ID_KEYS, instants: INSTANT_KEYS };

  public static readonly WorkCriteriaJSONShape = {
    ...optionalEach(TEXT_KEYS, z.string()),
    ...optionalEach(ID_KEYS, z.guid()),
    ...optionalEach(INSTANT_KEYS, z.iso.datetime({ offset: true })),
    ...optionalEach(YES_NO_KEYS, z.boolean()),
    ...optionalEach(AMOUNT_KEYS, z.number()),
    teamSide: z.enum(WorkTeamSide).optional(),
    teamRole: z.enum(WorkTeamRole).optional(),
    feeRateCurrency: z.enum(Currency).optional(),
  };

  public static readonly WorkCriteriaShape = {
    ...optionalEach(TEXT_KEYS, z.string()),
    ...optionalEach(ID_KEYS, z.instanceof(Guid)),
    ...optionalEach(INSTANT_KEYS, DateSchema.type),
    ...optionalEach(YES_NO_KEYS, z.boolean()),
    ...optionalEach(AMOUNT_KEYS, z.number()),
    teamSide: z.enum(WorkTeamSide).optional(),
    teamRole: z.enum(WorkTeamRole).optional(),
    feeRateCurrency: z.enum(Currency).optional(),
  };
}
